/**
 * CATs List Page
 *
 * Page for listing and managing CAT records.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { CatFilters, CatFiltersData, CatStatusChip, CatAccidentTypeChip } from './components';
import {
  Cat,
  CatFilters as CatFiltersType,
  formatCatDate,
  canCatBeEdited,
  canCatBeCancelled,
  canCatBeSubmitted,
} from '../../types/cat';
import { catService } from '../../services/catService';
import { openSignedFile } from '../../services/fileAccess';

const columns: DataTableColumn<Cat>[] = [
  { id: 'id', label: 'ID', render: (cat) => cat.id },
  {
    id: 'employee',
    label: 'Funcionário',
    primary: true,
    render: (cat) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {cat.employee?.name || '-'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          CPF: {cat.employee?.cpf || '-'}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'accidentDate',
    label: 'Data do Acidente',
    render: (cat) => formatCatDate(cat.accidentDate),
  },
  {
    id: 'accidentType',
    label: 'Tipo de Acidente',
    render: (cat) => <CatAccidentTypeChip type={cat.accidentType} />,
  },
  {
    id: 'status',
    label: 'Status',
    render: (cat) => <CatStatusChip status={cat.status} />,
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (cat) => (
      <Typography variant="body2">
        {cat.company?.tradeName || cat.company?.legalName || '-'}
      </Typography>
    ),
  },
];

const CatsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const editar = useControle('/cats/:id/edit', pode('cat.update'), 'Editar');
  const visualizar = useAcao('cat.read', 'Visualizar');
  const imprimir = useAcao('cat.read', 'Imprimir');
  const enviar = useAcao('cat.update', 'Enviar');
  const cancelar = useAcao('cat.update', 'Cancelar');

  const [cats, setCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<CatFiltersType>({});

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [catToCancel, setCatToCancel] = useState<Cat | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Submit dialog
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [catToSubmit, setCatToSubmit] = useState<Cat | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCats();
  }, [page, rowsPerPage]);

  const loadCats = async (filters?: CatFiltersType) => {
    setLoading(true);
    setError(null);
    try {
      const response = await catService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        filters: filters || currentFilters,
      });
      const catsData = Array.isArray(response) ? response : (response.data ?? []);
      const total = Array.isArray(response) ? response.length : (response.pagination?.total || 0);
      setCats(catsData);
      setTotalCount(total);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar CATs');
      enqueueSnackbar('Erro ao carregar CATs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (filters: CatFiltersData) => {
    const apiFilters: CatFiltersType = {
      search: filters.search ?? undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      reportType: filters.reportType || undefined,
      accidentType: filters.accidentType || undefined,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
    };
    setCurrentFilters(apiFilters);
    setPage(0);
    loadCats(apiFilters);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleView = (cat: Cat) => {
    navigate(`/cats/${cat.id}`);
  };

  const handleEdit = (cat: Cat) => {
    navigate(`/cats/${cat.id}/edit`);
  };

  const handlePrint = async (cat: Cat) => {
    // Open the new tab synchronously inside the click handler so the popup
    // blocker does not swallow it after the async PDF + token round-trips.
    const target = window.open('about:blank', '_blank');
    if (!target) {
      enqueueSnackbar('Permita popups para visualizar o PDF', { variant: 'warning' });
      return;
    }

    try {
      const { pdfPath } = await catService.generatePdf(cat.id);
      await openSignedFile(pdfPath, target);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar PDF';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleCancelClick = (cat: Cat) => {
    setCatToCancel(cat);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!catToCancel) return;

    setCancelling(true);
    try {
      await catService.cancel(catToCancel.id);
      enqueueSnackbar('CAT cancelada com sucesso', { variant: 'success' });
      setCancelDialogOpen(false);
      setCatToCancel(null);
      loadCats();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao cancelar CAT', { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitClick = (cat: Cat) => {
    setCatToSubmit(cat);
    setSubmitDialogOpen(true);
  };

  const handleSubmitConfirm = async () => {
    if (!catToSubmit) return;

    setSubmitting(true);
    try {
      await catService.submit(catToSubmit.id);
      enqueueSnackbar('CAT enviada com sucesso', { variant: 'success' });
      setSubmitDialogOpen(false);
      setCatToSubmit(null);
      loadCats();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao enviar CAT', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'CAT' },
  ];

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="CAT - Comunicação de Acidente de Trabalho"
      actions={
        <RouteActionButton
          para="/cats/new"
          exige={pode('cat.create')}
          variant="contained"
          startIcon={<AddIcon />}
        >
          Nova CAT
        </RouteActionButton>
      }
    >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <CatFilters onFilter={handleFilter} loading={loading} total={totalCount} />
        </Box>

        {/* Table */}
        <DataTable
          columns={columns}
          rows={cats}
          getRowId={(cat) => cat.id}
          loading={loading}
          emptyState={{
            title: 'Nenhuma CAT encontrada',
            description: 'A CAT é aberta nesta tela a partir do acidente de trabalho de um funcionário do seu acesso.',
          }}
          actions={[
            {
              icon: <ViewIcon fontSize="small" />,
              onClick: handleView,
              ...visualizar,
            },
            {
              icon: <PrintIcon fontSize="small" />,
              onClick: handlePrint,
              ...imprimir,
            },
            {
              icon: <EditIcon fontSize="small" />,
              onClick: handleEdit,
              hidden: (cat) => !canCatBeEdited(cat.status),
              ...editar,
            },
            {
              icon: <SendIcon fontSize="small" />,
              color: 'primary',
              onClick: handleSubmitClick,
              hidden: (cat) => !canCatBeSubmitted(cat.status),
              ...enviar,
            },
            {
              icon: <CancelIcon fontSize="small" />,
              color: 'error',
              onClick: handleCancelClick,
              hidden: (cat) => !canCatBeCancelled(cat.status),
              ...cancelar,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
          }}
        />

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Confirmar Cancelamento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja cancelar a CAT do funcionário "{catToCancel?.employee?.name}"?
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              Voltar
            </Button>
            <Button
              onClick={handleCancelConfirm}
              color="error"
              variant="contained"
              disabled={cancelling}
            >
              {cancelling ? 'Cancelando...' : 'Cancelar CAT'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit Confirmation Dialog */}
        <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
          <DialogTitle>Confirmar Envio</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja enviar a CAT do funcionário "{catToSubmit?.employee?.name}"?
              Após o envio, a CAT não poderá mais ser editada.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitDialogOpen(false)} disabled={submitting}>
              Voltar
            </Button>
            <Button
              onClick={handleSubmitConfirm}
              color="primary"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? 'Enviando...' : 'Enviar CAT'}
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default CatsListPage;
